import React, { useState } from "react";
import { Box, Button, Stack, Textarea, Typography } from "@mui/joy";
import { theme } from "styles/theme";
import type {
  ActionRequest,
  AssistantStep,
  Decision,
  PendingApprovalData,
  ReviewConfig,
} from "./types";
import type {
  TaskEditorActionComponent,
  TaskEditorBlock,
  TaskEditorDescriptionComponent,
} from "../utils/taskEditorUtils";
import {
  asBlocks,
  asPlanText,
  getActionKind,
  splitReviewFeedback,
} from "./approvalContent";

const MONO = "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace";

// 채팅 인풋(chatInput.tsx)과 동일한 얇은 포커스 — Joy 의 2px 포커스 링(::before)을
// 통째로 끄고, 1px 보더만 직접 그려 포커스 시 색만 Border.Highlight 로 바꾼다.
const THIN_FOCUS = {
  "--Textarea-focusedHighlight": "transparent",
  border: `1px solid ${theme.vars.palette.Border.Weak}`,
  "&::before": { display: "none" },
  "&:focus-within": { borderColor: theme.vars.palette.Border.Highlight },
} as const;

// propose_plan / update_task → 사람이 읽을 짧은 라벨 (감사 로그용).
function actionDisplayName(name: string): string {
  if (name === "propose_plan") return "계획";
  if (name === "update_task") return "활동지";
  return name;
}

// =========================
// ApprovalDecisionRow — 사용자가 내린 결정 표시
// =========================

export function ApprovalDecisionRow({
  step,
}: {
  step: Extract<AssistantStep, { kind: "approval_decision" }>;
}) {
  const decisionLabel =
    step.decision.type === "approve"
      ? "✓ 승인"
      : step.decision.type === "edit"
        ? "✎ 직접 수정"
        : step.decision.type === "revise"
          ? "↻ 수정 요청"
          : "거부";
  // revise/reject 는 거절이 아니라 자연어 재작성 요청·turn 종료 — danger(빨강) 어감 안 쓴다.
  // reject 는 사용자가 의도적으로 끄는 보조 액션이라 더 차분한 Weak 색으로.
  const decisionColor =
    step.decision.type === "approve"
      ? "#15803d"
      : step.decision.type === "edit"
        ? theme.vars.palette.primary.solid
        : step.decision.type === "revise"
          ? theme.vars.palette.Content.Medium
          : theme.vars.palette.Content.Weak;
  const trailingText =
    step.decision.type === "revise"
      ? step.decision.message
      : step.decision.type === "reject"
        ? step.decision.reason
        : undefined;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        px: "8px",
        py: "4px",
        fontSize: "11px",
        color: theme.vars.palette.Content.Weak,
      }}
    >
      <Box
        component="span"
        sx={{
          color: decisionColor,
          fontWeight: 700,
        }}
      >
        {decisionLabel}
      </Box>
      <Box
        component="span"
        sx={{
          color: theme.vars.palette.Content.Medium,
        }}
      >
        {actionDisplayName(step.originalAction.name)}
      </Box>
      {trailingText && (
        <Box
          component="span"
          sx={{
            fontStyle: "italic",
            color: theme.vars.palette.Content.Weak,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
            minWidth: 0,
          }}
        >
          “{trailingText}”
        </Box>
      )}
    </Box>
  );
}

// =========================
// ApprovalCard
// =========================

// 카드 헤더 라벨 — action 종류 / 검토 의견 유무 / 계획 선행 여부로 결정.
function cardHeaderLabel(
  data: PendingApprovalData,
  precededByPlan: boolean
): string {
  const first = data.action_requests[0];
  const kind = first ? getActionKind(first.name) : "unknown";
  if (kind === "plan") return "📋 계획 검토 · 1/2단계";
  if (kind === "task") {
    const hasReview =
      !!first && splitReviewFeedback(first.description).review !== null;
    if (hasReview) return "활동지 검토 (개선안)";
    if (precededByPlan) return "활동지 검토 · 2/2단계";
    return "활동지 검토";
  }
  return "승인 필요";
}

export function ApprovalCard({
  data,
  onDecide,
  precededByPlan = false,
}: {
  data: PendingApprovalData;
  onDecide: (decisions: Decision[]) => void;
  // 같은 turn 에서 propose_plan 승인이 선행됐는지 — update_task 카드의 2/2 단계 표기용.
  precededByPlan?: boolean;
}) {
  return (
    <Stack
      spacing={1.5}
      sx={{
        p: "12px",
        mt: "4px",
        borderRadius: "8px",
        backgroundColor: theme.vars.palette.Background.Level1,
        border: `1px solid ${theme.vars.palette.primary.solid}`,
        animation: "approvalPop 0.3s ease",
        "@keyframes approvalPop": {
          from: { opacity: 0, transform: "scale(0.98)" },
          to: { opacity: 1, transform: "scale(1)" },
        },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box
          sx={{
            width: "6px",
            height: "6px",
            borderRadius: "50%",
            backgroundColor: theme.vars.palette.primary.solid,
            animation: "pulse 1.5s ease infinite",
            "@keyframes pulse": {
              "0%, 100%": { opacity: 1 },
              "50%": { opacity: 0.4 },
            },
          }}
        />
        <Typography
          level="body-xs"
          sx={{
            fontSize: "11px",
            fontWeight: 700,
            color: theme.vars.palette.primary.solid,
            letterSpacing: "0.04em",
          }}
        >
          {cardHeaderLabel(data, precededByPlan)}
        </Typography>
      </Stack>
      {data.action_requests.map((action, idx) => (
        <ApprovalItem
          key={idx}
          action={action}
          config={data.review_configs[idx]}
          onApprove={() => onDecide([{ type: "approve" }])}
          onEdit={(editedArgs) =>
            onDecide([
              {
                type: "edit",
                edited_action: { name: action.name, args: editedArgs },
              },
            ])
          }
          onRevise={(message) => onDecide([{ type: "revise", message }])}
          onReject={(reason) =>
            onDecide([{ type: "reject", ...(reason ? { reason } : {}) }])
          }
        />
      ))}
    </Stack>
  );
}

// args 의 top-level string field 중 JSON 객체/배열로 parse 되는 것은 inline 객체로
// 펼쳐서 보여준다. 레거시 write_file.content 처럼 stringified JSON 을 그대로 escape 된
// 한 줄 string 으로 보여주는 것을 막기 위함. 저장 시 inlinedKeys 로 다시 string 화한다.
function expandJsonStringFields(args: Record<string, unknown>): {
  displayText: string;
  inlinedKeys: string[];
} {
  const expanded: Record<string, unknown> = {};
  const inlinedKeys: string[] = [];
  for (const [key, value] of Object.entries(args)) {
    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        if (parsed !== null && typeof parsed === "object") {
          expanded[key] = parsed;
          inlinedKeys.push(key);
          continue;
        }
      } catch {
        // not JSON — fall through
      }
    }
    expanded[key] = value;
  }
  return { displayText: JSON.stringify(expanded, null, 2), inlinedKeys };
}

function collapseJsonStringFields(
  edited: Record<string, unknown>,
  inlinedKeys: string[]
): Record<string, unknown> {
  const result: Record<string, unknown> = { ...edited };
  for (const key of inlinedKeys) {
    const v = result[key];
    if (v !== undefined && typeof v !== "string") {
      result[key] = JSON.stringify(v, null, 2);
    }
  }
  return result;
}

function ApprovalItem({
  action,
  config,
  onApprove,
  onEdit,
  onRevise,
  onReject,
}: {
  action: ActionRequest;
  config?: ReviewConfig;
  onApprove: () => void;
  onEdit: (args: Record<string, unknown>) => void;
  onRevise: (message: string) => void;
  onReject: (reason: string) => void;
}) {
  const kind = getActionKind(action.name);
  const [mode, setMode] = useState<"idle" | "edit" | "revise" | "reject">(
    "idle"
  );
  const [planText, setPlanText] = useState(() => asPlanText(action.args) ?? "");
  const [{ argsText, inlinedKeys }, setArgsState] = useState(() => {
    const { displayText, inlinedKeys: keys } = expandJsonStringFields(
      action.args
    );
    return { argsText: displayText, inlinedKeys: keys };
  });
  const [reviseMessage, setReviseMessage] = useState("");
  const [rejectReason, setRejectReason] = useState("");

  const allowed = config?.allowed_decisions ?? [
    "approve",
    "edit",
    "revise",
    "reject",
  ];

  // task(update_task) 의 description 에는 task_reviewer 검토 의견이 동봉될 수 있다.
  const { body, review } =
    kind === "task"
      ? splitReviewFeedback(action.description)
      : { body: action.description, review: null };

  return (
    <Stack spacing={1}>
      {/* 본문 설명 — 계획은 인용 박스로 따로 보여주므로 중복 출력하지 않는다. */}
      {kind !== "plan" && body && (
        <Typography
          level="body-sm"
          sx={{
            fontSize: "13px",
            color: theme.vars.palette.Content.Medium,
            lineHeight: 1.5,
            whiteSpace: "pre-wrap",
          }}
        >
          {body}
        </Typography>
      )}

      {/* 미리보기 — action 종류별 분기 */}
      {kind === "plan" && (
        <Stack spacing={0.75}>
          <Typography
            level="body-xs"
            sx={{
              fontSize: "12px",
              color: theme.vars.palette.Content.Medium,
            }}
          >
            이 구성으로 만들까요?
          </Typography>
          <PlanBox text={asPlanText(action.args) ?? action.description} />
        </Stack>
      )}
      {kind === "task" && (
        <>
          {review && <ReviewBox text={review} />}
          <BlocksPreview args={action.args} />
        </>
      )}
      {kind === "unknown" && <GenericArgsPreview args={action.args} />}

      {mode === "idle" && (
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          alignItems="center"
          sx={{ flexWrap: "wrap" }}
        >
          {/* 거부 — 사용자가 의도적으로 끄는 보조 액션. 직접 수정과 같은 outlined neutral 로 톤 맞춤. */}
          {allowed.includes("reject") && (
            <Button
              size="sm"
              variant="outlined"
              color="neutral"
              onClick={() => setMode("reject")}
              sx={{ minHeight: "30px", fontSize: "12px" }}
            >
              ✕ 거부
            </Button>
          )}
          {allowed.includes("edit") && (
            <Button
              size="sm"
              variant="outlined"
              color="neutral"
              onClick={() => setMode("edit")}
              sx={{ minHeight: "30px", fontSize: "12px" }}
            >
              ✎ 직접 수정
            </Button>
          )}
          {allowed.includes("revise") && (
            <Button
              size="sm"
              variant="outlined"
              color="primary"
              onClick={() => setMode("revise")}
              sx={{ minHeight: "30px", fontSize: "12px" }}
            >
              ↻ 수정 요청
            </Button>
          )}
          {allowed.includes("approve") && (
            <Button
              size="sm"
              variant="solid"
              color="primary"
              onClick={onApprove}
              sx={{ minHeight: "30px", fontSize: "12px", fontWeight: 600 }}
            >
              ✓ 승인
            </Button>
          )}
        </Stack>
      )}

      {/* edit — 계획은 텍스트 편집, 그 외(활동지/미지)는 JSON 편집 */}
      {mode === "edit" && kind === "plan" && (
        <Stack spacing={1}>
          <Textarea
            value={planText}
            onChange={(e) => setPlanText(e.target.value)}
            minRows={4}
            maxRows={14}
            sx={{ fontSize: "13px", lineHeight: 1.6, ...THIN_FOCUS }}
          />
          <Stack direction="row" spacing={1}>
            <Button
              size="sm"
              variant="plain"
              color="neutral"
              onClick={() => setMode("idle")}
              sx={{ minHeight: "28px", fontSize: "12px" }}
            >
              취소
            </Button>
            <Button
              size="sm"
              variant="solid"
              color="primary"
              onClick={() => onEdit({ plan: planText })}
              sx={{ minHeight: "28px", fontSize: "12px" }}
            >
              수정 후 적용
            </Button>
          </Stack>
        </Stack>
      )}

      {mode === "edit" && kind !== "plan" && (
        <Stack spacing={1}>
          <Textarea
            value={argsText}
            onChange={(e) =>
              setArgsState((prev) => ({
                ...prev,
                argsText: e.target.value,
              }))
            }
            minRows={6}
            maxRows={14}
            sx={{ fontFamily: MONO, fontSize: "11px", ...THIN_FOCUS }}
          />
          <Stack direction="row" spacing={1}>
            <Button
              size="sm"
              variant="plain"
              color="neutral"
              onClick={() => setMode("idle")}
              sx={{ minHeight: "28px", fontSize: "12px" }}
            >
              취소
            </Button>
            <Button
              size="sm"
              variant="solid"
              color="primary"
              onClick={() => {
                try {
                  const parsed = JSON.parse(argsText);
                  onEdit(collapseJsonStringFields(parsed, inlinedKeys));
                } catch {
                  // ignore parse error — leave UI open
                }
              }}
              sx={{ minHeight: "28px", fontSize: "12px" }}
            >
              수정 후 적용
            </Button>
          </Stack>
        </Stack>
      )}

      {/* 수정 요청(revise) — 자연어로 "이렇게 고쳐서 다시"를 agent 에 전달 */}
      {mode === "revise" && (
        <Stack spacing={1}>
          <Textarea
            value={reviseMessage}
            onChange={(e) => setReviseMessage(e.target.value)}
            placeholder="어떻게 바꿀까요?"
            minRows={2}
            maxRows={5}
            sx={{ fontSize: "12px", ...THIN_FOCUS }}
          />
          <Stack direction="row" spacing={1}>
            <Button
              size="sm"
              variant="plain"
              color="neutral"
              onClick={() => setMode("idle")}
              sx={{ minHeight: "28px", fontSize: "12px" }}
            >
              취소
            </Button>
            <Button
              size="sm"
              variant="solid"
              color="primary"
              disabled={!reviseMessage.trim()}
              onClick={() => onRevise(reviseMessage.trim())}
              sx={{ minHeight: "28px", fontSize: "12px" }}
            >
              수정 요청 보내기
            </Button>
          </Stack>
        </Stack>
      )}

      {/* 거부(reject) — turn 종료. 사유는 선택. */}
      {mode === "reject" && (
        <Stack spacing={1}>
          <Textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="거부 사유 (선택). 비워둬도 됩니다."
            minRows={2}
            maxRows={5}
            sx={{ fontSize: "12px", ...THIN_FOCUS }}
          />
          <Stack direction="row" spacing={1}>
            <Button
              size="sm"
              variant="plain"
              color="neutral"
              onClick={() => setMode("idle")}
              sx={{ minHeight: "28px", fontSize: "12px" }}
            >
              취소
            </Button>
            <Button
              size="sm"
              variant="outlined"
              color="neutral"
              onClick={() => onReject(rejectReason.trim())}
              sx={{ minHeight: "28px", fontSize: "12px" }}
            >
              거부하기
            </Button>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}

// 계획(propose_plan) 텍스트 — 균일한 1px 보더 인용 박스, 줄바꿈 보존.
// (카드 전체가 이미 primary 보더라 왼쪽 강조 띠는 과해서 제거)
function PlanBox({ text }: { text: string }) {
  return (
    <Box
      sx={{
        borderRadius: "6px",
        border: `1px solid ${theme.vars.palette.Border.Weak}`,
        backgroundColor: theme.vars.palette.Background.Level2,
        p: "10px 12px",
        fontSize: "13px",
        lineHeight: 1.6,
        color: theme.vars.palette.Content.Strong,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      }}
    >
      {text}
    </Box>
  );
}

// task_reviewer 검토 의견 — amber 좌측 보더로 본문/계획과 시각 구분.
function ReviewBox({ text }: { text: string }) {
  return (
    <Box
      sx={{
        borderRadius: "6px",
        border: "1px solid rgba(217, 119, 6, 0.3)",
        borderLeft: "3px solid #d97706",
        backgroundColor: "rgba(217, 119, 6, 0.06)",
        p: "8px 10px",
      }}
    >
      <Box
        sx={{
          fontSize: "11px",
          fontWeight: 700,
          color: "#b45309",
          mb: "4px",
          letterSpacing: "0.02em",
        }}
      >
        📝 task_reviewer 검토 의견
      </Box>
      <Box
        sx={{
          fontSize: "12px",
          lineHeight: 1.6,
          color: theme.vars.palette.Content.Medium,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {text}
      </Box>
    </Box>
  );
}

// update_task 의 blocks 미리보기 — 검토가 가능하도록 블록별 전체 텍스트를 보여준다
// (잘라내지 않음). 기본은 펼친 상태, 길면 헤더 토글로 접을 수 있다.
function BlocksPreview({ args }: { args: Record<string, unknown> }) {
  const [expanded, setExpanded] = useState(true);
  const blocks = asBlocks(args);
  // blocks 형태가 어긋나면(미지 도구 등) raw JSON fallback.
  if (!blocks) return <GenericArgsPreview args={args} />;

  return (
    <Box>
      <Box
        onClick={() => setExpanded((v) => !v)}
        sx={{
          fontSize: "10px",
          fontWeight: 600,
          color: theme.vars.palette.Content.Weak,
          cursor: "pointer",
          letterSpacing: "0.04em",
          mb: "4px",
          userSelect: "none",
        }}
      >
        {expanded ? "▾" : "▸"} {blocks.length}개 블록
      </Box>
      {expanded && (
        <Stack
          sx={{
            borderRadius: "6px",
            border: `1px solid ${theme.vars.palette.Border.Weak}`,
            backgroundColor: theme.vars.palette.Background.Level2,
            overflow: "hidden",
          }}
        >
          {blocks.map((block, i) => (
            <BlockRow
              key={i}
              block={block}
              seq={i + 1}
              last={i === blocks.length - 1}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
}

function BlockRow({
  block,
  seq,
  last,
}: {
  block: TaskEditorBlock;
  seq: number;
  last: boolean;
}) {
  const isHeader = block.type === "header";
  const actionType = block.action?.type ?? null;
  return (
    <Box
      sx={{
        p: "8px 10px",
        borderBottom: last ? 0 : `1px solid ${theme.vars.palette.Border.Weak}`,
      }}
    >
      {/* 메타 라인: 순번 · 헤더/본문 · action 종류 */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          mb: "6px",
          flexWrap: "wrap",
        }}
      >
        <Box
          component="span"
          sx={{
            fontFamily: MONO,
            fontSize: "10px",
            color: theme.vars.palette.Content.Weak,
            flexShrink: 0,
          }}
        >
          {seq}
        </Box>
        <Box
          component="span"
          sx={{
            fontSize: "9px",
            fontWeight: 700,
            letterSpacing: "0.04em",
            px: "5px",
            py: "1px",
            borderRadius: "4px",
            border: `1px solid ${
              isHeader
                ? theme.vars.palette.primary.solid
                : theme.vars.palette.Border.Weak
            }`,
            color: isHeader
              ? theme.vars.palette.primary.solid
              : theme.vars.palette.Content.Weak,
            flexShrink: 0,
          }}
        >
          {isHeader ? "헤더" : "본문"}
        </Box>
        {actionType && (
          <Box
            component="span"
            sx={{
              fontFamily: MONO,
              fontSize: "10px",
              color: theme.vars.palette.primary.solid,
              flexShrink: 0,
            }}
          >
            {actionType}
          </Box>
        )}
      </Box>
      {/* 전체 본문 — 잘라내지 않고 wrap. text 는 typography 에 따라 굵기 차등. */}
      <Stack spacing={0.5} sx={{ fontSize: "12px", lineHeight: 1.55 }}>
        {block.description.map((c, i) =>
          c.type === "text" ? (
            <Box
              key={i}
              sx={{
                fontWeight:
                  c.typography_style === "header"
                    ? 700
                    : c.typography_style === "label"
                      ? 600
                      : 400,
                color: theme.vars.palette.Content.Strong,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {c.content}
            </Box>
          ) : (
            <Box
              key={i}
              sx={{
                color: theme.vars.palette.Content.Weak,
                fontStyle: "italic",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {describeComponent(c)}
            </Box>
          )
        )}
        {block.action && (
          <Box
            sx={{
              mt: "2px",
              color: theme.vars.palette.primary.solid,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {describeAction(block.action)}
          </Box>
        )}
      </Stack>
    </Box>
  );
}

function describeComponent(c: TaskEditorDescriptionComponent): string {
  switch (c.type) {
    case "text":
      return c.content;
    case "image":
      return "🖼 이미지";
    case "reference_automatic_thought":
      return "↪ 자동사고 불러오기";
    case "reference_coach_preset":
      return "↪ 코치 프리셋: " + c.preset_text;
    default:
      return "";
  }
}

function describeAction(a: TaskEditorActionComponent): string {
  switch (a.type) {
    case "text_input":
      return `✏ 텍스트 입력 · ${a.fields.length}칸`;
    case "button_default":
      return (
        "◻ 버튼" +
        (a.allow_multiple_selection ? " (복수)" : "") +
        ": " +
        a.options.map((o) => o.text).join(" / ")
      );
    case "button_quiz":
      return (
        "❓ 퀴즈: " +
        a.options.map((o) => (o.is_correct ? "✓ " : "") + o.text).join(" / ")
      );
    case "button_branch":
      return "⑃ 분기: " + a.options.map((o) => o.text).join(" / ");
    default:
      return "";
  }
}

// 미지 도구(레거시 write_file mock 등) — 접이식 raw JSON. 하드 크래시 방지용 fallback.
function GenericArgsPreview({ args }: { args: Record<string, unknown> }) {
  const [expanded, setExpanded] = useState(false);
  const { displayText } = expandJsonStringFields(args);
  if (!displayText || displayText === "{}") return null;
  return (
    <Box>
      <Box
        onClick={() => setExpanded((v) => !v)}
        sx={{
          fontSize: "10px",
          fontWeight: 600,
          color: theme.vars.palette.Content.Weak,
          cursor: "pointer",
          letterSpacing: "0.04em",
          mb: "4px",
          userSelect: "none",
        }}
      >
        {expanded ? "▾" : "▸"} 인자
      </Box>
      {expanded && (
        <Box
          sx={{
            p: "8px 10px",
            borderRadius: "4px",
            border: `1px solid ${theme.vars.palette.Border.Weak}`,
            backgroundColor: theme.vars.palette.Background.Level2,
            fontFamily: MONO,
            fontSize: "11px",
            lineHeight: 1.5,
            maxHeight: "240px",
            overflow: "auto",
            whiteSpace: "pre",
          }}
        >
          {displayText}
        </Box>
      )}
    </Box>
  );
}
